import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Calendar, X } from "lucide-react";
import { useState, useEffect } from "react";
import { trackPhoneCall, trackTextMessage, trackBookingStart } from "@/components/GoogleAnalytics";

interface StickyBookingCTAProps {
  onBookingClick: () => void;
}

const StickyBookingCTA = ({ onBookingClick }: StickyBookingCTAProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA after user scrolls past hero section (approximately 1000px)
      // This prevents overlap with hero section city buttons
      setIsVisible(window.scrollY > 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookingClick = async () => {
    setIsLoading(true);
    trackBookingStart();
    
    // Small delay to show loading state
    setTimeout(() => {
      setIsLoading(false);
      onBookingClick();
    }, 500);
  };

  const handlePhoneClick = () => {
    trackPhoneCall();
    window.open('tel:+16167270343', '_self');
  };

  const handleTextClick = () => {
    trackTextMessage();
    window.open('sms:+16167270343', '_self');
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Mobile Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg md:hidden">
        <div className="flex items-center justify-between p-3 gap-2">
          <Button
            onClick={handlePhoneClick}
            variant="outline"
            size="sm"
            className="flex-1 gap-2"
          >
            <Phone className="w-4 h-4" />
            Call
          </Button>
          <Button
            onClick={handleTextClick}
            variant="outline"
            size="sm"
            className="flex-1 gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            Text
          </Button>
          <Button
            onClick={handleBookingClick}
            disabled={isLoading}
            className="flex-1 luxury-gradient text-white gap-2"
            size="sm"
          >
            {isLoading ? (
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Calendar className="w-4 h-4" />
            )}
            Book Now
          </Button>
        </div>
      </div>

      {/* Desktop Floating CTA */}
      <div className="fixed bottom-6 right-6 z-50 hidden md:block">
        {!isExpanded ? (
          // Collapsed state - just the main button
          <Button
            onClick={() => setIsExpanded(true)}
            className="luxury-gradient text-white shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-6 text-lg font-medium rounded-full"
            size="lg"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Now
          </Button>
        ) : (
          // Expanded state - multiple options
          <div className="bg-background border border-border rounded-2xl shadow-xl p-4 min-w-[280px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Quick Contact</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-2">
              <Button
                onClick={handleBookingClick}
                disabled={isLoading}
                className="w-full luxury-gradient text-white gap-2"
                size="lg"
              >
                {isLoading ? (
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )}
                Book Consultation
              </Button>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={handlePhoneClick}
                  variant="outline"
                  className="gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </Button>
                <Button
                  onClick={handleTextClick}
                  variant="outline"
                  className="gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Text
                </Button>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3 text-center">
              (616) 727-0343 • Available 7 days a week
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default StickyBookingCTA;